'use client';

import { useMemo, useState } from 'react';
import { SettingsContentPanel } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useExternalNetworkSurfaceDetail } from '../../../hooks/api/useSurfaceSettings';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  EXTERNAL_NETWORK_SETTINGS_TABS,
  getVisibleExternalNetworkSettingsTabs,
  resolveExternalNetworkSettingsActiveTab,
} from './externalNetworkSettings.constants';
import { NetworkSettingsShell } from './NetworkSettingsShell';
import { ExternalGeneralSettingsPanel } from './external/ExternalGeneralSettingsPanel';
import { ExternalBusinessProfileSettingsPanel } from './external/ExternalBusinessProfileSettingsPanel';
import { ExternalAssetsSettingsPanel } from './external/ExternalAssetsSettingsPanel';
import { ExternalCloudAccountsSettingsPanel } from './external/ExternalCloudAccountsSettingsPanel';
import { ExternalWebAppsSettingsPanel } from './external/ExternalWebAppsSettingsPanel';
import { ExternalDnsZonesSettingsPanel } from './external/ExternalDnsZonesSettingsPanel';
import { ExternalAutomationsSettingsPanel } from './external/ExternalAutomationsSettingsPanel';
import { ExternalDangerSettingsPanel } from './external/ExternalDangerSettingsPanel';

type ExternalNetworkSettingsTabProps = {
  surfaceId: string | null;
};

export function ExternalNetworkSettingsTab({
  surfaceId,
}: ExternalNetworkSettingsTabProps) {
  const t = useTranslations('externalNetwork.settings');
  const tMainContent = useTranslations('proxy.mainContentArea');
  const [selectedTab, setSelectedTab] = useState<string>(
    EXTERNAL_NETWORK_SETTINGS_TABS.GENERAL,
  );
  const {
    data: surface,
    isLoading,
    isError,
  } = useExternalNetworkSurfaceDetail(surfaceId);
  const { canManageApplications } = usePermissions();
  const { isFeatureFlagEnabled: isCloudInventoryEnabled } = useFeatureFlags(
    'EXTERNAL_NETWORK_CLOUD_INVENTORY_ENABLED',
  );
  const { isFeatureFlagEnabled: isWebAppDraftsEnabled } = useFeatureFlags(
    'EXTERNAL_NETWORK_WEB_APP_DRAFTS_ENABLED',
  );
  const { isFeatureFlagEnabled: isDnsZoneSyncEnabled } = useFeatureFlags(
    'EXTERNAL_NETWORK_DNS_ZONE_SYNC_ENABLED',
  );
  const isConnectorSurface = surface?.egressStrategy === 'CONNECTOR';
  const visibleTabs = useMemo(
    () =>
      getVisibleExternalNetworkSettingsTabs({
        isConnectorSurface,
        isCloudInventoryEnabled,
        isWebAppDraftsEnabled,
        isDnsZoneSyncEnabled,
        canManageApplications,
      }),
    [
      isConnectorSurface,
      isCloudInventoryEnabled,
      isWebAppDraftsEnabled,
      isDnsZoneSyncEnabled,
      canManageApplications,
    ],
  );
  const navItems = useMemo(
    () => visibleTabs.map((tab) => ({ value: tab, label: t(`tabs.${tab}`) })),
    [t, visibleTabs],
  );
  const activeTab = resolveExternalNetworkSettingsActiveTab({
    activeTab: selectedTab,
    visibleTabs,
  });
  const isTabVisible = (tab: string) => visibleTabs.some((id) => id === tab);

  if (!surfaceId) {
    return (
      <NetworkSettingsShell
        title={t('title')}
        navItems={navItems}
        activeNavItem={activeTab}
        onNavItemChange={setSelectedTab}
        emptyStateMessage={tMainContent('selectApplicationToGetStarted')}
      >
        {null}
      </NetworkSettingsShell>
    );
  }

  return (
    <NetworkSettingsShell
      title={t('title')}
      navItems={navItems}
      activeNavItem={activeTab}
      onNavItemChange={setSelectedTab}
      isLoading={isLoading}
      errorMessage={isError ? t('fetchError') : null}
    >
      {surface ? (
        <>
          <SettingsContentPanel
            value={EXTERNAL_NETWORK_SETTINGS_TABS.GENERAL}
            activeValue={activeTab}
          >
            <ExternalGeneralSettingsPanel
              surface={surface}
              surfaceId={surfaceId}
              isReadOnly={!canManageApplications}
            />
          </SettingsContentPanel>
          {isTabVisible(EXTERNAL_NETWORK_SETTINGS_TABS.BUSINESS_PROFILE) ? (
            <SettingsContentPanel
              value={EXTERNAL_NETWORK_SETTINGS_TABS.BUSINESS_PROFILE}
              activeValue={activeTab}
            >
              <ExternalBusinessProfileSettingsPanel surfaceId={surfaceId} />
            </SettingsContentPanel>
          ) : null}
          <SettingsContentPanel
            value={EXTERNAL_NETWORK_SETTINGS_TABS.ASSETS}
            activeValue={activeTab}
          >
            <ExternalAssetsSettingsPanel surfaceId={surfaceId} />
          </SettingsContentPanel>
          {isTabVisible(EXTERNAL_NETWORK_SETTINGS_TABS.CLOUD_ACCOUNTS) ? (
            <SettingsContentPanel
              value={EXTERNAL_NETWORK_SETTINGS_TABS.CLOUD_ACCOUNTS}
              activeValue={activeTab}
            >
              <ExternalCloudAccountsSettingsPanel surfaceId={surfaceId} />
            </SettingsContentPanel>
          ) : null}
          {isTabVisible(EXTERNAL_NETWORK_SETTINGS_TABS.WEB_APPS) ? (
            <SettingsContentPanel
              value={EXTERNAL_NETWORK_SETTINGS_TABS.WEB_APPS}
              activeValue={activeTab}
            >
              <ExternalWebAppsSettingsPanel
                surface={surface}
                surfaceId={surfaceId}
              />
            </SettingsContentPanel>
          ) : null}
          {isTabVisible(EXTERNAL_NETWORK_SETTINGS_TABS.DNS_ZONES) ? (
            <SettingsContentPanel
              value={EXTERNAL_NETWORK_SETTINGS_TABS.DNS_ZONES}
              activeValue={activeTab}
            >
              <ExternalDnsZonesSettingsPanel surfaceId={surfaceId} />
            </SettingsContentPanel>
          ) : null}
          <SettingsContentPanel
            value={EXTERNAL_NETWORK_SETTINGS_TABS.AUTOMATIONS}
            activeValue={activeTab}
          >
            <ExternalAutomationsSettingsPanel
              surface={surface}
              surfaceId={surfaceId}
              isReadOnly={!canManageApplications}
            />
          </SettingsContentPanel>
          {isTabVisible(EXTERNAL_NETWORK_SETTINGS_TABS.DANGER) ? (
            <SettingsContentPanel
              value={EXTERNAL_NETWORK_SETTINGS_TABS.DANGER}
              activeValue={activeTab}
            >
              <ExternalDangerSettingsPanel
                surfaceId={surfaceId}
                surfaceName={surface.name}
              />
            </SettingsContentPanel>
          ) : null}
        </>
      ) : null}
    </NetworkSettingsShell>
  );
}
